import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded';
import AppShell from './components/AppShell';
import { useAuth } from './context/AuthContext';
import api from './services/api';
import PartnerQuotaTable from './quotas/PartnerQuotaTable';
import QuotaTable from './quotas/QuotaTable';
import { SYSTEM_LABELS } from './quotas/trackerSeries';

/**
 * A quota view. Partners see one system's quotas (the view's "Partners see" setting), which the
 * server alone chooses, in a plain table. Stack users see every quota, from both systems.
 */
const FilteredQuotaPage = () => {
  const { quotaMonitorId } = useParams();
  const { user } = useAuth();
  const isStack = user?.role === 'stack';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get(`/quota-monitors/${quotaMonitorId}/quotas/`)
      .then((res) => setData({
        ...res.data,
        quotas: res.data.quotas.map((q) => ({ ...q, limit: Number(q.limit), responses: Number(q.responses) })),
      }))
      .catch(() => setError('Failed to fetch quotas.'))
      .finally(() => setLoading(false));
  }, [quotaMonitorId]);

  if (loading) return (
    <AppShell>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    </AppShell>
  );
  if (error) return <Typography color="error" sx={{ mt: 4, ml: 4 }}>{error}</Typography>;

  // Stack only: a failed system's quotas are missing, so say so rather than show a short list.
  const partial = isStack && Object.values(data.systems || {}).some((s) => s.status === 'error');
  const partnerCount = isStack ? data.quotas.filter((q) => q.system === data.partner_system).length : 0;

  return (
    <AppShell>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <LeaderboardRoundedIcon color="primary" />
        <Typography variant="h4" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
          Quota View
        </Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 4 }}>ID: {quotaMonitorId}</Typography>

      <Box
        sx={{
          bgcolor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: 3,
          p: 3,
        }}
      >
        {isStack && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Partners see only this view&apos;s {SYSTEM_LABELS[data.partner_system]} quotas ({partnerCount}), with name,
            description, completes, limit and progress. Change it in Quota Monitoring.
          </Alert>
        )}
        {partial && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Some quotas couldn&apos;t be loaded just now and are missing from this table. Try again in a few minutes.
          </Alert>
        )}
        {isStack ? <QuotaTable quotas={data.quotas} /> : <PartnerQuotaTable quotas={data.quotas} />}
      </Box>
    </AppShell>
  );
};

export default FilteredQuotaPage;
